import { AlertTriangle, CheckCircle2, CircleDashed, Clock, XCircle } from "lucide-react";

import ExtendedBadge from "@/components/ExtendedBadge";
import { isReturnedForCorrection, RETURNED_FOR_CORRECTION_LABEL } from "@/utils/application-status";
import type {
    ApprovalCommitteeFeedback,
    ApprovalPhaseStep,
    UnifiedApproval,
} from "../../services/applications-services";

/**
 * Shows the applicant where their application actually stands in the ZCT
 * approval workflow: the current stage, every phase the backend reports, and —
 * the part that was previously invisible to customers — the reviewer's own
 * comment, name and decision date for each phase.
 *
 * Everything rendered here comes from `UnifiedApprovalDTO`. Nothing is filled
 * in when the backend has no value for it: a phase with no activity says so.
 */

const REJECTED_PATTERN = /reject|declin|fail|cancel/i;
const APPROVED_PATTERN = /approv|pass|complet|issued/i;
const PENDING_PATTERN = /pending|progress|review|processing|assign|clarification|draft|feedback/i;

type Tone = "success" | "danger" | "warning" | "neutral";

function statusTone(...statuses: (string | null | undefined)[]): Tone {
    const status = statuses.find(hasText);
    if (!status) return "neutral";
    if (REJECTED_PATTERN.test(status)) return "danger";
    if (APPROVED_PATTERN.test(status)) return "success";
    if (PENDING_PATTERN.test(status)) return "warning";
    return "neutral";
}

function hasText(value: string | null | undefined): value is string {
    return typeof value === "string" && value.trim() !== "";
}

/** Turns SCREAMING_SNAKE backend statuses into something a customer can read. */
function humanizeStatus(value: string | null | undefined): string | undefined {
    if (!hasText(value)) return undefined;
    if (!/[a-z]/.test(value)) {
        return value
            .toLowerCase()
            .split(/[_\s]+/)
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    }
    return value;
}

function formatDate(value: string | null | undefined): string | undefined {
    if (!hasText(value)) return undefined;
    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

function cleanList(items: (string | null | undefined)[] | null | undefined): string[] {
    return (items ?? []).filter(hasText).map((item) => item.trim());
}

const TONE_ICON: Record<Tone, React.ReactNode> = {
    success: <CheckCircle2 className="size-5 text-green-600" />,
    danger: <XCircle className="size-5 text-red-600" />,
    warning: <Clock className="size-5 text-yellow-600" />,
    neutral: <CircleDashed className="size-5 text-gray-400" />,
};

type PhaseBullets = { heading: string; items: string[] };

type PhaseView = {
    key: string;
    title: string;
    /** What the person in this phase is called, e.g. "Reviewed by". */
    actorLabel: string;
    status?: string | null;
    simpleStatus?: string | null;
    comment?: string | null;
    actor?: string | null;
    decidedOn?: string | null;
    bullets: PhaseBullets[];
    steps: ApprovalPhaseStep[];
    committeeFeedbacks: ApprovalCommitteeFeedback[];
};

function phaseHasActivity(phase: PhaseView): boolean {
    return (
        hasText(phase.status) ||
        hasText(phase.comment) ||
        hasText(phase.actor) ||
        hasText(phase.decidedOn) ||
        phase.bullets.some((bullet) => bullet.items.length > 0) ||
        phase.steps.length > 0 ||
        phase.committeeFeedbacks.length > 0
    );
}

function buildPhases(approval: UnifiedApproval): PhaseView[] {
    const phases: PhaseView[] = [];

    const legal = approval.legalPhase;
    if (legal) {
        phases.push({
            key: "legal",
            title: "Legal review",
            actorLabel: "Reviewed by",
            status: legal.status,
            simpleStatus: legal.simpleStatus,
            comment: legal.comment,
            actor: legal.reviewedBy,
            decidedOn: legal.reviewDate,
            bullets: [{ heading: "Findings", items: cleanList(legal.findings) }],
            steps: legal.steps ?? [],
            committeeFeedbacks: [],
        });
    }

    const inspection = approval.inspectionPhase;
    if (inspection) {
        const inspectionRecord = inspection.inspection;
        phases.push({
            key: "inspection",
            title: "Inspection",
            actorLabel: "Inspected by",
            status: inspection.status,
            simpleStatus: inspection.simpleStatus,
            comment: inspection.comment ?? inspectionRecord?.feedback,
            actor: cleanList(inspectionRecord?.inspectorNames).join(", ") || undefined,
            decidedOn: inspection.inspectionDate ?? inspectionRecord?.completedDate,
            bullets: [
                { heading: "Violations recorded", items: cleanList(inspectionRecord?.violations) },
                { heading: "Recommendations", items: cleanList(inspectionRecord?.recommendations) },
            ],
            steps: inspection.steps ?? [],
            committeeFeedbacks: (inspection.committeeFeedbacks ?? []).filter(Boolean),
        });

        const inspectionDecision = inspection.finalDecision;
        if (inspectionDecision) {
            phases.push({
                key: "inspection-decision",
                title: "Inspection decision",
                actorLabel: "Decided by",
                status: inspectionDecision.status,
                simpleStatus: inspectionDecision.simpleStatus,
                comment: inspectionDecision.comment,
                actor: inspectionDecision.decidedBy,
                decidedOn: inspectionDecision.decisionDate,
                bullets: [],
                steps: [],
                committeeFeedbacks: [],
            });
        }
    }

    const documents = approval.documentPhase;
    if (documents) {
        const findings = documents.documentFindings ?? [];
        phases.push({
            key: "documents",
            title: "Document review",
            actorLabel: "Reviewed by",
            status: documents.status,
            simpleStatus: documents.simpleStatus,
            comment: undefined,
            actor: undefined,
            decidedOn: undefined,
            bullets: [
                {
                    heading: "Reviewer notes on your documents",
                    items: findings
                        .filter((finding) => hasText(finding?.comments))
                        .map((finding) => {
                            const state = finding.approved ? "Approved" : "Not approved";
                            const followUp = finding.requiresFollowUp ? " — follow-up required" : "";
                            return `${state}${followUp}: ${finding.comments}`;
                        }),
                },
            ],
            steps: documents.steps ?? [],
            committeeFeedbacks: [],
        });
    }

    const final = approval.finalApprovalPhase;
    if (final) {
        phases.push({
            key: "final",
            title: "Final decision",
            actorLabel: "Decided by",
            status: final.status,
            simpleStatus: final.simpleStatus,
            comment: final.comment,
            actor: final.decidedBy,
            decidedOn: final.decisionDate,
            bullets: [{ heading: "Conditions attached to this decision", items: cleanList(final.conditions) }],
            steps: final.steps ?? [],
            committeeFeedbacks: [],
        });
    }

    return phases;
}

function PhaseRow({ phase, isLast }: { phase: PhaseView; isLast: boolean }) {
    const active = phaseHasActivity(phase);
    const tone = active ? statusTone(phase.status, phase.simpleStatus) : "neutral";
    const isRejection = tone === "danger";
    const statusLabel = humanizeStatus(phase.simpleStatus) ?? humanizeStatus(phase.status);
    const decidedOn = formatDate(phase.decidedOn);

    return (
        <div className="flex items-stretch gap-4">
            <section className="min-h-full flex flex-col items-center gap-2">
                {TONE_ICON[tone]}
                {!isLast && <div className="flex-1 w-0.5 bg-secondary-100 rounded-sm" />}
            </section>
            <section className={isLast ? "flex-1" : "flex-1 pb-6"}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-semibold text-[#2C2C2C]">{phase.title}</h3>
                    {active && statusLabel ? (
                        <ExtendedBadge
                            variant={tone === "neutral" ? "primary" : tone}
                            label={statusLabel}
                        />
                    ) : (
                        <span className="text-xs text-gray-500">Not started yet</span>
                    )}
                </div>

                {active && (
                    <>
                        {(hasText(phase.actor) || decidedOn) && (
                            <p className="mt-1 text-sm text-gray-600">
                                {hasText(phase.actor) && (
                                    <span>
                                        {phase.actorLabel}{" "}
                                        <span className="font-medium text-[#3C1E03]">{phase.actor}</span>
                                    </span>
                                )}
                                {hasText(phase.actor) && decidedOn && <span> · </span>}
                                {decidedOn && <span>on {decidedOn}</span>}
                            </p>
                        )}

                        {hasText(phase.comment) && (
                            <div
                                className={`mt-2 rounded-lg border p-3 ${
                                    isRejection
                                        ? "border-red-200 bg-red-50"
                                        : "border-secondary-100 bg-[#f5f2f0]"
                                }`}
                            >
                                <p
                                    className={`text-xs font-semibold uppercase tracking-wide ${
                                        isRejection ? "text-red-700" : "text-gray-500"
                                    }`}
                                >
                                    {isRejection ? "Reason for rejection" : "Reviewer's comment"}
                                </p>
                                <p
                                    className={`mt-1 whitespace-pre-line text-sm ${
                                        isRejection ? "text-red-800" : "text-gray-700"
                                    }`}
                                >
                                    {phase.comment}
                                </p>
                            </div>
                        )}

                        {phase.bullets
                            .filter((bullet) => bullet.items.length > 0)
                            .map((bullet) => (
                                <div key={bullet.heading} className="mt-2">
                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                        {bullet.heading}
                                    </p>
                                    <ul className="mt-1 list-disc space-y-1 pl-5 text-sm text-gray-700">
                                        {bullet.items.map((item, index) => (
                                            <li key={`${bullet.heading}-${index}`}>{item}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}

                        {phase.committeeFeedbacks.length > 0 && (
                            <div className="mt-2 space-y-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    Committee feedback
                                </p>
                                {phase.committeeFeedbacks.map((feedback, index) => {
                                    const raisedOn = formatDate(feedback.discussionDate ?? feedback.createdAt);
                                    return (
                                        <div
                                            key={feedback.id ?? index}
                                            className="rounded-lg border border-secondary-100 p-3"
                                        >
                                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                                {hasText(feedback.feedbackType) && (
                                                    <span className="font-semibold text-[#3C1E03]">
                                                        {humanizeStatus(feedback.feedbackType)}
                                                    </span>
                                                )}
                                                {hasText(feedback.status) && (
                                                    <span>{humanizeStatus(feedback.status)}</span>
                                                )}
                                                {raisedOn && <span>{raisedOn}</span>}
                                            </div>
                                            {hasText(feedback.comment) && (
                                                <p className="mt-1 whitespace-pre-line text-sm text-gray-700">
                                                    {feedback.comment}
                                                </p>
                                            )}
                                            {hasText(feedback.createdBy) && (
                                                <p className="mt-1 text-xs text-gray-500">
                                                    Raised by {feedback.createdBy}
                                                </p>
                                            )}
                                            {feedback.requiresResponse && (
                                                <p className="mt-1 flex items-center gap-1 text-xs font-medium text-yellow-700">
                                                    <AlertTriangle className="size-3.5" /> A response is required from you
                                                </p>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {phase.steps.length > 0 && (
                            <ul className="mt-2 space-y-1 text-xs text-gray-500">
                                {phase.steps.map((step, index) => {
                                    const completedOn = formatDate(step.completedAt);
                                    return (
                                        <li key={step.name ?? index}>
                                            {humanizeStatus(step.name) ?? `Step ${step.stepNumber ?? index + 1}`}
                                            {hasText(step.status) && ` — ${humanizeStatus(step.status)}`}
                                            {hasText(step.completedBy) && ` by ${step.completedBy}`}
                                            {completedOn && ` on ${completedOn}`}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </>
                )}
            </section>
        </div>
    );
}

export type ApprovalProgressPanelProps = {
    approval?: UnifiedApproval | null;
    isLoading?: boolean;
    isError?: boolean;
    /** Optional heading override, e.g. "Licence Approval Progress". */
    title?: string;
};

export default function ApprovalProgressPanel({
    approval,
    isLoading,
    isError,
    title = "Approval Progress",
}: ApprovalProgressPanelProps) {
    return (
        <div>
            <h1 className="my-2 text-lg font-semibold text-[#3C1E03]">{title}</h1>
            <div className="bg-white rounded-lg p-6 shadow-sm">
                <ApprovalProgressBody approval={approval} isLoading={isLoading} isError={isError} />
            </div>
        </div>
    );
}

function ApprovalProgressBody({
    approval,
    isLoading,
    isError,
}: Pick<ApprovalProgressPanelProps, "approval" | "isLoading" | "isError">) {
    if (isLoading) {
        return (
            <div className="flex items-center gap-3 text-sm text-gray-500">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
                Loading approval status…
            </div>
        );
    }

    if (isError) {
        return (
            <p className="text-sm text-gray-600">
                We could not load the approval status for this application right now. Please try again later.
            </p>
        );
    }

    if (!approval) {
        return (
            <p className="text-sm text-gray-600">
                This application has not entered the approval workflow yet, so there is nothing to show.
            </p>
        );
    }

    const phases = buildPhases(approval);
    const activePhases = phases.filter(phaseHasActivity);
    // `simpleStatus` reports the "Processing" default for a returned application —
    // mapToSimpleStatus has no case for RETURNED_FOR_CORRECTION — so the raw
    // `currentStatus` has to win here, or the panel would contradict the banner
    // telling the applicant to act.
    const returned = isReturnedForCorrection(approval.currentStatus);
    const currentStage = returned
        ? RETURNED_FOR_CORRECTION_LABEL
        : humanizeStatus(approval.simpleStatus) ?? humanizeStatus(approval.currentStatus);
    const tone: Tone = returned ? "warning" : statusTone(approval.simpleStatus, approval.currentStatus);
    const completed = approval.completedPhases ?? 0;
    const total = approval.totalPhases ?? 0;

    return (
        <>
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-secondary-100 pb-4">
                <div>
                    <p className="text-gray-600">Current stage</p>
                    {currentStage ? (
                        <p className="text-lg font-bold text-[#2C2C2C]">{currentStage}</p>
                    ) : (
                        <p className="text-sm text-gray-500">Not reported yet</p>
                    )}
                    {hasText(approval.applicationNumber) && approval.applicationNumber !== "N/A" && (
                        <p className="mt-1 text-xs text-gray-500">
                            Application no. {approval.applicationNumber}
                        </p>
                    )}
                </div>
                <div className="flex flex-col items-start gap-2 sm:items-end">
                    {currentStage && (
                        <ExtendedBadge
                            variant={tone === "neutral" ? "primary" : tone}
                            label={currentStage}
                        />
                    )}
                    {total > 0 && (
                        <span className="text-xs text-gray-500">
                            {completed} of {total} phases completed
                        </span>
                    )}
                </div>
            </div>

            {activePhases.length === 0 ? (
                <p className="pt-4 text-sm text-gray-600">
                    No approval activity has been recorded yet. Once a reviewer looks at your application,
                    their decision and comments will appear here.
                </p>
            ) : (
                <div className="pt-4">
                    {phases.map((phase, index) => (
                        <PhaseRow
                            key={phase.key}
                            phase={phase}
                            isLast={index === phases.length - 1}
                        />
                    ))}
                </div>
            )}
        </>
    );
}
